// redux/slices/dropdownSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import axios from 'axios';
import { selectDropdownValues } from './criticalDataSlice';
import { portalApi } from '../api/portalApi';

// Cache duration for user dropdowns (4 hours - user lists change infrequently)
const USER_DROPDOWN_CACHE_DURATION = 4 * 60 * 60 * 1000;

// Global flag to prevent concurrent Graph API calls
let isUserDropdownFetchInProgress = false;

const USER_DROPDOWN_KEYS = [
  'mechOptions',
  'appsOptions',
  'elecOptions',
  'controlOptions',
  'adminOptions',
  'salesOptions',
];

const sortUserDropdownOptions = (options = []) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return [...options].sort((a, b) => {
    const displayNameA = (a?.displayName || '').trim();
    const displayNameB = (b?.displayName || '').trim();
    return displayNameA.localeCompare(displayNameB, undefined, { sensitivity: 'base' });
  });
};

const sortUserDropdownGroups = (userDropdowns = {}) =>
  USER_DROPDOWN_KEYS.reduce((sortedGroups, key) => {
    sortedGroups[key] = sortUserDropdownOptions(userDropdowns[key]);
    return sortedGroups;
  }, {});

/**
 * DEPRECATED: fetchDropdownValues - Dropdown values now come from criticalDataSlice
 * This thunk is kept for backward compatibility but returns data from criticalDataSlice
 * Components should fetch critical data using fetchAllCriticalData instead
 * 
 * This now acts as a pass-through that returns empty data since the selector
 * will automatically read from criticalData when it's available
 */
export const fetchDropdownValues = createAsyncThunk(
  'dropdown/fetchDropdownValues',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Get dropdown values from criticalDataSlice
      const state = getState();
      const dropdownValues = selectDropdownValues(state);
      
      if (dropdownValues && dropdownValues.length > 0) {
        // Sort the data by sort order before returning
        const sortedData = [...dropdownValues].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
        return { data: sortedData, fromCache: true };
      }
      
      // Return empty data - the selector will pull from criticalData when it's loaded
      // No warning needed since this is handled automatically by App.js calling fetchAllCriticalData
      return { data: [], fromCache: false };
    } catch (error) {
      console.error('fetchDropdownValues: Failed to get dropdown values from criticalDataSlice:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch user dropdown options from MS Graph API
export const fetchUserDropdowns = createAsyncThunk(
  'dropdown/fetchUserDropdowns',
  async ({ instance, accounts }, { getState, rejectWithValue }) => {
    // Prevent concurrent calls - return cached data if fetch already in progress
    if (isUserDropdownFetchInProgress) {
      const { dropdown } = getState();
      return { data: sortUserDropdownGroups(dropdown.userDropdowns), fromCache: true };
    }
    
    const { dropdown } = getState();
    const now = Date.now();
    
    // Validate cache: Check if we have meaningful data in all categories
    const hasValidData = dropdown.userDropdowns && 
      (dropdown.userDropdowns.mechOptions?.length > 0 ||
       dropdown.userDropdowns.appsOptions?.length > 0 ||
       dropdown.userDropdowns.elecOptions?.length > 0 ||
       dropdown.userDropdowns.controlOptions?.length > 0 ||
       dropdown.userDropdowns.adminOptions?.length > 0 ||
       dropdown.userDropdowns.salesOptions?.length > 0);
    
    // Check if we have cached data that's still valid
    if (hasValidData && 
        dropdown.lastUserDropdownFetch && 
        (now - dropdown.lastUserDropdownFetch < USER_DROPDOWN_CACHE_DURATION)) {
      return { data: sortUserDropdownGroups(dropdown.userDropdowns), fromCache: true };
    }
    
    // Set global flag to prevent concurrent calls
    isUserDropdownFetchInProgress = true;
    
    try {
      const account = accounts?.[0];
      if (!account) {
        throw new Error('No account available for Graph dropdown fetch');
      }

      const response = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account,
      });

      const groupConfig = [
        { optionKey: 'mechOptions', groupId: process.env.REACT_APP_GROUP_MECH },
        { optionKey: 'appsOptions', groupId: process.env.REACT_APP_GROUP_APPS },
        { optionKey: 'elecOptions', groupId: process.env.REACT_APP_GROUP_ELEC },
        { optionKey: 'controlOptions', groupId: process.env.REACT_APP_GROUP_CONTROL },
        { optionKey: 'adminOptions', groupId: process.env.REACT_APP_GROUP_ADMIN },
        { optionKey: 'salesOptions', groupId: process.env.REACT_APP_GROUP_SALES },
      ];

      const batchRequests = groupConfig
        .filter(({ groupId }) => Boolean(groupId))
        .map(({ optionKey, groupId }) => ({
          id: optionKey,
          method: 'GET',
          url: `/groups/${groupId}/members?$select=id,displayName,mail&$top=999`,
        }));

      const batchResponse = await axios.post(
        'https://graph.microsoft.com/v1.0/$batch',
        { requests: batchRequests },
        {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const responseByKey = (batchResponse?.data?.responses || []).reduce((accumulator, entry) => {
        accumulator[entry.id] = entry;
        return accumulator;
      }, {});

      const mapGroupMembers = (optionKey) => {
        const groupResponse = responseByKey[optionKey];

        if (!groupResponse) {
          return [];
        }

        if (groupResponse.status >= 400) {
          throw new Error(`Failed to fetch ${optionKey} members (status ${groupResponse.status})`);
        }

        const members = Array.isArray(groupResponse?.body?.value) ? groupResponse.body.value : [];

        return members.map((member) => ({
          id: member.id,
          displayName: member.displayName,
          email: member.mail,
        }));
      };

      const mechOptions = mapGroupMembers('mechOptions');
      const appsOptions = mapGroupMembers('appsOptions');
      const elecOptions = mapGroupMembers('elecOptions');
      const controlOptions = mapGroupMembers('controlOptions');
      const adminOptions = mapGroupMembers('adminOptions');
      const salesOptions = mapGroupMembers('salesOptions');

      // Add "House" as a hardcoded option to salesOptions
      const salesOptionsWithHouse = [
        ...salesOptions,
        { displayName: "House", email: "" }
      ];

      const sortedUserDropdowns = sortUserDropdownGroups({
        mechOptions,
        appsOptions,
        elecOptions,
        controlOptions,
        adminOptions,
        salesOptions: salesOptionsWithHouse,
      });
      
      return { 
        data: sortedUserDropdowns,
        fromCache: false 
      };

    } catch (error) {
      console.error("Failed to fetch user dropdowns:", error.message);
      return rejectWithValue(error.message);
    } finally {
      // Always clear the global flag once this request has settled
      isUserDropdownFetchInProgress = false;
    }
  }
);

const dropdownSlice = createSlice({
  name: 'dropdown',
  initialState: {
    options: [],
    userDropdowns: sortUserDropdownGroups(), // User dropdowns
    loading: false,
    error: null,
    lastDropdownFetch: null,
    lastUserDropdownFetch: null
  },
  reducers: {
    replaceRealtimeDropdownValues: (state, action) => {
      state.options = [...action.payload].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
    },
    clearDropdowns: (state) => {
      state.options = [];
      state.userDropdowns = sortUserDropdownGroups();
      state.loading = false;
      state.error = null;
      state.lastDropdownFetch = null;
      state.lastUserDropdownFetch = null;
      // Clear global fetch flag
      isUserDropdownFetchInProgress = false;
    },
  },
  extraReducers: (builder) => {
    // For fetching static dropdowns from an API
    builder.addCase(fetchDropdownValues.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchDropdownValues.fulfilled, (state, action) => {
      state.loading = false;

      // Update the data whether from cache or fresh
      // This ensures components using state.dropdown.options directly still work
      state.options = action.payload.data;
      if (!action.payload.fromCache) {
        state.lastDropdownFetch = Date.now();
      }
    });

    builder.addCase(fetchDropdownValues.rejected, (state, action) => {
      console.error('Fetch dropdown values rejected:', action.payload); // Log error
      state.error = action.payload;
      state.loading = false;
    });

    // For fetching user dropdowns via MS Graph API
    builder.addCase(fetchUserDropdowns.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchUserDropdowns.fulfilled, (state, action) => {
      state.loading = false;

      state.userDropdowns = sortUserDropdownGroups(action.payload.data);

      // Only update fetch timestamp if it's not from cache
      if (!action.payload.fromCache) {
        state.lastUserDropdownFetch = Date.now();
      }
    });

    builder.addCase(fetchUserDropdowns.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Listen for RTK Query getCriticalData fulfillment to populate options array
    builder.addMatcher(
      portalApi.endpoints.getCriticalData.matchFulfilled,
      (state, action) => {
        const dropdownValues = action.payload?.DropdownValues;
        if (dropdownValues && dropdownValues.length > 0) {
          state.options = [...dropdownValues].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
          state.lastDropdownFetch = Date.now();
          console.log('dropdownSlice: Populated state.options from criticalData:', state.options.length, 'options');
        }
      }
    );
  },
});

// UPDATED SELECTORS: Now redirect to criticalDataSlice for dropdown values
const EMPTY_ARRAY = [];
const selectCriticalDropdownValues = (state) => state.criticalData?.data?.dropdownValues;
const selectLegacyDropdownOptions = (state) => state.dropdown.options;
const selectRawUserDropdowns = (state) => state.dropdown.userDropdowns;

export const selectDropdownOptions = createSelector(
  [selectCriticalDropdownValues, selectLegacyDropdownOptions],
  (criticalDropdowns, legacyDropdowns) => {
    if (Array.isArray(criticalDropdowns) && criticalDropdowns.length > 0) {
      return [...criticalDropdowns].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
    }

    if (Array.isArray(legacyDropdowns)) {
      return legacyDropdowns;
    }

    return EMPTY_ARRAY;
  }
);

export const selectUserDropdowns = createSelector(
  [selectRawUserDropdowns],
  (userDropdowns) => sortUserDropdownGroups(userDropdowns)
);
export const selectLastUserDropdownFetch = (state) => state.dropdown.lastUserDropdownFetch;
export const selectDropdownLoading = (state) => {
  // Combine loading states from both slices
  return state.dropdown.loading || state.criticalData?.loading || false;
};
export const selectDropdownError = (state) => {
  // Return error from either slice
  return state.dropdown.error || state.criticalData?.error || null;
};

export const { clearDropdowns } = dropdownSlice.actions;

export default dropdownSlice.reducer;

export const { replaceRealtimeDropdownValues } = dropdownSlice.actions;
